import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {
  createSearchParams,
  Link,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import LoginIcon from "@mui/icons-material/Login";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
//import HeaderMobileMenu from "./HeaderMobileMenu";
import HeaderProfile from "./HeaderProfile";
import HeaderProfileDrawerMenu from "./HeaderProfileDrawerMenu";
import {
  HeaderMenuNavDropdown,
  HeaderMenuContentDropdown,
} from "./HeaderMenuDropdown";
import {
  clearLocalStorage,
  checkLogin,
  getHasImpersonate,
  checkRewardEligibility,
  getIntercomUser,
  isRetailLoggedIn,
} from "../utils/jwt";
import {
  authMenu,
  unauthMenu,
  getProfileMenu,
} from "../constants/mainSiteNavigation";
import { SHOP_NOW } from "../constants/mainSiteNavigation";
import { getRewardInfo } from "../utils/rewards";
import { useGetUserById } from "../hooks/useMyAccount";
import { useRegenRewards } from "../hooks/useRegenRewards";
import { useGetStoreLogo, useStore, useUpdatedStore } from "../hooks/useStore";
import "../styles_web/Animations.css";
import css from "../styles_web/Header.module.css";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import { useGetCart } from "../hooks/useManageCart";
import { useQueryClient } from "react-query";
import GlyMedLogo from "../components/icons/GlymedLogo";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  CartDataState,
  RewardsNextTierMinSpendState,
} from "../atoms/cartAtoms";
import useHideCart from "../hooks/useHideCart";
import SpaLogo from "./SpaLogo";
import useNavMenu from "../hooks/useNavMenu";
import ImpersonationBanner from "../components/ImpersonationBanner";
import useProductsMenu from "../hooks/useProductsMenu";
import { useIntercom } from "react-use-intercom";
import Image from "../components/shared/Image";
import { viewCart_GA } from "../utils/GoogleAnalytics";

import { useRecoilValue } from "recoil";
import SelectedRetailerState from "../atoms/retailersAtom.js";

/************************************/
/* Header */
/************************************/
const Header = () => {
  const hideCart = useHideCart();
  const isLoggedIn = checkLogin();
  const isRetail = isRetailLoggedIn();
  const match = useMatch({ path: "/checkout", end: false });
  const { accountId: retailerId } = useRecoilValue(SelectedRetailerState) || {};
  const { accountId: storeAccountId } = useUpdatedStore();
  //used to indicate defined menu dropdowns
  const validDropdowns = ["Products", "About", "More"];

  //filter out menu items if user came through a store link
  const navMenu = useNavMenu(
    !isLoggedIn && (storeAccountId || retailerId)
      ? ["becomepro", "locations"]
      : []
  );

  const navRef = useRef(null);
  const moreRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [showDropdown, setShowDropdown] = useState("");
  const [applyMenuEffects, setApplyMenuEffects] = useState(true);
  const [mobileIsOpen, setMobileIsOpen] = useState(false);
  const [mobileShopIsOpen] = useState(false);
  const setNextTierMinSpend = useSetRecoilState(RewardsNextTierMinSpendState);
  const { boot, shutdown } = useIntercom();
  const { productDropdownMenu } = useProductsMenu();
  const [cartData, setCartData] = useRecoilState(CartDataState);

  const openMobileMenu = () => {
    setMobileIsOpen(true);
  };

  const closeMobileMenu = () => {
    setMobileIsOpen(false);
  };

  const closeDropdowns = useCallback(() => {
    if (showDropdown !== "") {
      setShowDropdown("");
    }
  }, [showDropdown, setShowDropdown]);

  const applyProductFilter = useCallback(
    (event, section) => {
      navigate({
        pathname: "/products",
        search: createSearchParams(
          isRetail ? { concerns: section } : { category: section }
        ).toString(),
      });
      closeDropdowns();
    },
    [navigate, closeDropdowns, isRetail]
  );

  const signOut = useCallback(async () => {
    closeMobileMenu();
    navigate("/login");
    clearLocalStorage();
    queryClient.clear();
    const userDetails = getIntercomUser();
    boot(userDetails);
  }, [navigate, queryClient, boot]);

  const signIn = useCallback(() => {
    closeMobileMenu();
    const { pathname } = location;
    if (
      pathname === "/register-retail" &&
      location?.state?.from?.pathname?.includes("recommendations")
    ) {
      navigate("/login", { state: location.state });
    } else {
      navigate("/login", { state: { from: { pathname } } });
    }
  }, [location, navigate]);

  const handleSignInOut = useCallback(() => {
    shutdown();
    if (!isLoggedIn) signIn();
    else signOut();
  }, [signIn, signOut, isLoggedIn, shutdown]);

  const navigateToRegisterUser = () => {
    location.state
      ? navigate("/register-retail", { state: location.state })
      : navigate("/register-retail");
  };

  const handleMenuItemClickOrHover = (e, item) => {
    setShowDropdown("");
    //open the dropdown (if it is valid/exists),
    //don't navigate the link for the menu item.
    if (validDropdowns.indexOf(item.title) > -1) {
      if (showDropdown !== item.title && e.type !== "mouseleave") {
        setShowDropdown(item.title);
      }
      e.preventDefault();
    }
  };

  const handleMenuItemProductsClick = () => {
    setShowDropdown("");
    navigate("/products");
  };

  const handleCartIconClick = () => {
    viewCart_GA(cartData);
    navigate(`/cart`, { state: { backgroundLocation: location } });
  };

  const { data } = useGetCart(!!match); // on checkout page, fetch full value of cart
  const { data: user } = useGetUserById();
  const storeId = isLoggedIn
    ? user?.proAccountId
    : retailerId
    ? retailerId
    : storeAccountId
    ? storeAccountId
    : null;
  const { data: store } = useStore(storeId);
  const { data: storeImage } = useGetStoreLogo(storeId);
  const { data: regenRewards } = useRegenRewards();

  //hook for removing top level menu animation effects to ensure dropdown nav submenus render correctly.
  //there is a browser rendering quirk that prevents absolute/fixed position elements from rendering
  //correctly if there are any scale animation used on the element or parent element.
  useEffect(() => {
    setTimeout(() => setApplyMenuEffects(false), 1000);
  });

  useEffect(() => {
    if (data) {
      setCartData(data);
    }
  }, [data, setCartData]);

  //hook for retrieving reward info
  const eligibleForRewards = checkRewardEligibility();
  const rewards = useMemo(() => {
    if (eligibleForRewards && regenRewards) {
      const rewardInfo = getRewardInfo(regenRewards);
      setNextTierMinSpend(rewardInfo?.nextTierSpend);
      return rewardInfo;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenRewards, eligibleForRewards]);

  //hook for retrieving profile info
  const profile = useMemo(() => {
    const { firstName, lastName, email } = user || {};
    const userName =
      firstName && lastName
        ? `${user.firstName} ${user.lastName}`
        : email
        ? email
        : "";
    const storeName = store ? store.accountName : null;
    const storeLogo = storeImage && storeImage?.data ? storeImage.data : null;
    const profileMenu = getProfileMenu(user);

    return {
      userName,
      storeName,
      storeLogo,
      title: profileMenu.title,
      menu: profileMenu.menu,
    };
  }, [user, store, storeImage]);

  //hook for handling size/scroll/mouse events
  useEffect(() => {
    function handleClickOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setShowDropdown("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    const resized = (e) => {
      const width =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;
      //sized to mobile
      if (width < 1200) closeDropdowns();
    };
    window.addEventListener("resize", resized);
    window.addEventListener("resize", closeMobileMenu);
    window.addEventListener("scroll", closeDropdowns);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", resized);
      window.removeEventListener("resize", closeMobileMenu);
      window.removeEventListener("scroll", closeDropdowns);
    };
  }, [navRef, showDropdown, mobileIsOpen, mobileShopIsOpen, closeDropdowns]);

  return (
    <nav className={css["fixed-top"]} ref={navRef}>
      <div className={css["header-container"]}>
        {getHasImpersonate() ? <ImpersonationBanner /> : null}
        <div className={css["nav-content"]}>
          {/* ======================================== */}
          {/* Mobile Menu Toggle and Menu */}
          {/* ======================================== */}
          <div
            className={classNames(
              [css["hamburger-container"]],
              "scale-in-center-delay-1"
            )}
          >
            <label onClick={openMobileMenu} className={css["header-label"]}>
              <span />
              <span />
              <span />
            </label>
          </div>

          {/*<HeaderMobileMenu
            isOpen={mobileIsOpen}
            onClosed={closeMobileMenu}
            onSignInOutClick={handleSignInOut}
            ></HeaderMobileMenu>*/}

          {isLoggedIn && profile && (
            <HeaderProfileDrawerMenu
              isOpen={mobileIsOpen}
              onClosed={closeMobileMenu}
              onSignInOut={handleSignInOut}
              userName={profile.userName}
              storeName={profile.storeName}
              storeLogo={profile.storeLogo}
              profileTitle={profile.title}
              profileMenu={profile.menu}
              rewards={rewards}
              navMenu={navMenu}
              authMenu={authMenu}
            />
          )}

          {!isLoggedIn && (
            <HeaderProfileDrawerMenu
              isOpen={mobileIsOpen}
              onClosed={closeMobileMenu}
              onSignInOut={handleSignInOut}
              navMenu={navMenu}
              authMenu={unauthMenu}
              storeName={profile.storeName}
              storeLogo={profile.storeLogo}
            />
          )}

          <div
            className={classNames(
              [css["logo-box"]],
              { [css["logo-box-signedin"]]: isLoggedIn },
              "scale-in-center-delay-0"
            )}
          >
            <Link to="/">
              <GlyMedLogo />
            </Link>
          </div>

          {/* ======================================== */}
          {/* Desktop Menu */}
          {/* ======================================== */}
          <div
            className={classNames([css["nav-link-box"]], {
              "scale-in-center-delay-4": applyMenuEffects,
            })}
          >
            <ul>
              {navMenu.map((item) => (
                <HeaderMenuItem
                  key={item.title}
                  text={item.title}
                  link={item.link ?? "/"}
                  hasDropdown={item.menuItems || item.title === "Products"}
                  onClick={(e) =>
                    item.id === "products"
                      ? handleMenuItemProductsClick()
                      : handleMenuItemClickOrHover(e, item)
                  }
                  onMouseEnter={
                    item.isExtendedMenu
                      ? (e) => handleMenuItemClickOrHover(e, item)
                      : null
                  }
                  onMouseLeave={
                    item.isExtendedMenu
                      ? (e) => handleMenuItemClickOrHover(e, item)
                      : null
                  }
                  showArrow={item.isExtendedMenu}
                >
                  {/* Products Dropdown */}
                  {item.title === "Products" && (
                    <HeaderMenuContentDropdown
                      className={classNames({
                        [css["show-shop"]]: showDropdown === "Products",
                      })}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <div className={css["grid-container"]}>
                              <div className={css["grid-content"]}>
                                <Grid container sx={{ pl: 6 }}>
                                  <Grid container item spacing={4}>
                                    {productDropdownMenu
                                      ?.slice(0, 4)
                                      ?.map((item) => (
                                        <ProductCategory
                                          isRetail={isRetail}
                                          key={item.id}
                                          subMenu={item}
                                          applyProductFilter={
                                            applyProductFilter
                                          }
                                        />
                                      ))}
                                    <Grid item xs={2.4}>
                                      <div
                                        className={classNames(
                                          [css["grid-img"]],
                                          "stagger-animation",
                                          [css["nav-link-button"]]
                                        )}
                                      >
                                        <Button
                                          variant="btn-primary"
                                          onClick={() => {
                                            setShowDropdown("");
                                          }}
                                          to="/products"
                                        >
                                          {`${SHOP_NOW}\u00a0\u00a0>`}
                                        </Button>
                                      </div>
                                    </Grid>
                                    {productDropdownMenu
                                      ?.slice(4)
                                      ?.map((item) => (
                                        <ProductCategory
                                          isRetail={isRetail}
                                          key={item.id}
                                          subMenu={item}
                                          applyProductFilter={
                                            applyProductFilter
                                          }
                                        />
                                      ))}
                                  </Grid>
                                </Grid>
                              </div>
                            </div>
                          </Grid>
                        </Grid>
                      </Box>
                    </HeaderMenuContentDropdown>
                  )}

                  {/* About Submenu Dropdown */}
                  {item.title === "About" && (
                    <HeaderMenuNavDropdown
                      className={classNames({
                        [css["show-about"]]: showDropdown === "About",
                      })}
                      menuItems={
                        navMenu.find((item) => item.title === "About").menuItems
                      }
                      onItemClick={() => closeDropdowns()}
                    ></HeaderMenuNavDropdown>
                  )}

                  {/* More Submenu Dropdown */}
                  {item.title === "More" && (
                    <HeaderMenuNavDropdown
                      ref={moreRef}
                      className={classNames({
                        [css["show-more"]]: showDropdown === "More",
                      })}
                      menuItems={
                        navMenu.find((item) => item.title === "More").menuItems
                      }
                      onItemClick={() => closeDropdowns()}
                    ></HeaderMenuNavDropdown>
                  )}
                </HeaderMenuItem>
              ))}
            </ul>
          </div>

          <div className={css["nav-right-box"]}>
            {/*****************************************/}
            {/* Store Label For Non Logged in User    */}
            {/****************************************/}

            {!isLoggedIn && (storeAccountId || retailerId) && (
              <>
                <SpaLogo
                  storeName={profile?.storeName}
                  storeLogo={profile?.storeLogo}
                />
                <Button
                  variant="btn-text"
                  onClick={navigateToRegisterUser}
                  sx={{
                    display: {
                      xs: "none",
                      sm: "inline-flex",
                      marginRight: "10px",
                    },
                  }}
                >
                  Register
                </Button>
              </>
            )}
            {/************************************/}
            {/* Sign In Button                   */}
            {/************************************/}
            {!isLoggedIn && (
              <Button
                variant="btn-text"
                onClick={signIn}
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                }}
              >
                Sign In
              </Button>
            )}

            {!isLoggedIn && (
              <div onClick={signIn}>
                <IconButton
                  aria-label="login"
                  size="large"
                  sx={{
                    minWidth: "3rem",
                    cursor: "pointer",
                    display: { xs: "inline-flex", sm: "none" },
                  }}
                >
                  <LoginIcon />
                </IconButton>
              </div>
            )}

            {/************************************/}
            {/* Signed In User Profile/Menu      */}
            {/************************************/}
            {isLoggedIn && profile && (
              <HeaderProfile
                userName={profile?.userName}
                storeName={profile.storeName}
                storeLogo={profile.storeLogo}
                profileTitle={profile.title}
                profileMenu={profile.menu}
                rewards={rewards}
                authMenu={authMenu}
                onSignInOut={handleSignInOut}
              ></HeaderProfile>
            )}

            {/************************************/}
            {/* Cart Button                      */}
            {/************************************/}

            {!hideCart && (
              <div className={css["cart-button"]} onClick={handleCartIconClick}>
                <IconButton
                  aria-label="cart"
                  size="large"
                  sx={{ minWidth: "3rem", cursor: "pointer" }}
                >
                  {cartData && (
                    <Badge
                      badgeContent={cartData?.orderTotal?.numItems}
                      color="error"
                    >
                      <ShoppingCartOutlinedIcon />
                    </Badge>
                  )}
                </IconButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

/************************************/
/* Header Menu Item (Desktop)       */
/************************************/
const HeaderMenuItem = ({
  children,
  text,
  link,
  hasDropdown,
  showArrow,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  return (
    <li>
      {hasDropdown && (
        <>
          <span
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={classNames([css["nav-link"]], [css["nav-link-button"]])}
          >
            {text}
            {showArrow && <span className={css["more-arrow"]}></span>}
            {/* child items must be inside of element with mouse actions for them to work properly */}
            {onMouseEnter && onMouseLeave && children}
          </span>
          {/* leave child items out for anything else, hover effects can have unintended style effects */}
          {!(onMouseEnter && onMouseLeave) && children}
        </>
      )}
      {link && !hasDropdown && (
        <Link
          to={link}
          alt={text}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={classNames([css["nav-link"]], [css["nav-link-button"]])}
        >
          {text}
        </Link>
      )}
    </li>
  );
};

/************************************/
/* Product Category (Desktop)       */
/************************************/
const ProductCategory = ({ subMenu, key, applyProductFilter, isRetail }) => {
  if (subMenu?.title && subMenu?.media) {
    return (
      <Grid item xs={2.4}>
        <div
          className={classNames([css["grid-img"]], "stagger-animation", [
            css["nav-link-product-sub-menu"],
          ])}
          onClick={(e) => {
            e.stopPropagation();
            applyProductFilter(e, subMenu.filter);
          }}
        >
          <div
            className={
              isRetail ? css["product-img-retail"] : css["product-img"]
            }
          >
            <Image src={subMenu.media} alt={subMenu.title} />
          </div>
          <div>{subMenu.title}</div>
        </div>
      </Grid>
    );
  }
};

HeaderMenuItem.propTypes = {
  text: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  showArrow: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

export default React.memo(Header);
